import flexysureLogo from 'assets/flexysure/logo.png'
import flexysureLogoTransparent from 'assets/flexysure/logo-transparent.png'
import lucentLogo from 'assets/flexysure/lucent-logo.png'
import insuranceImage from 'assets/shared/insuranceImage.svg'
import type { TenantConfig, TenantSeedTheme } from '../types'
import {
  generateTheme,
  generateDarkTheme,
  generateColors
} from '../generateTheme'

const seed: TenantSeedTheme = {
  primary: '290 72% 35%',
  secondary: '39 92% 59%',
  emailPrimaryHex: '#84199A',
  emailSecondaryHex: '#F7B336'
}

const flexysureConfig: TenantConfig = {
  id: 'flexysure',
  name: 'FlexySure',
  tagline: 'African Insurance Aggregator',
  logo: flexysureLogo,
  logoTransparent: flexysureLogoTransparent,
  authImage: insuranceImage,
  authBrandBackground: '#f5f0ff',
  favicon: '/favicon.png',
  poweredBy: {
    name: 'Lucent',
    logo: lucentLogo,
    url: 'https://lucent.ke'
  },
  websiteUrl: 'https://flexysure.com',
  supportEmail: 'flexysure@lucent.ke',
  supportPhone: '0709 527 000',
  twitterHandle: '@FlexySure',
  theme: generateTheme(seed),
  darkTheme: generateDarkTheme(seed),
  colors: generateColors('#84199A', '#F7B336'),
  content: {
    heroTitle: "We're here to fix insurance",
    heroTitleAccent: 'Yes, really!',
    heroDescription:
      'Nobody likes insurance. But everybody needs it. So we made it simple: Get covered in minutes, not days. No paperwork. No nonsense. Just protection that works when you need it.',
    ratingText: '4.8/5 from 100,000+ customers',
    stats: [
      { value: '100K+', label: 'Happy Customers' },
      { value: '50+', label: 'Insurance Partners' },
      { value: '24/7', label: 'Customer Support' },
      { value: '99.9%', label: 'Uptime Guarantee' }
    ],
    howItWorksTitle: 'How It Works',
    howItWorksSubtitle:
      'Get your motor insurance in three simple steps — no paperwork, no waiting.',
    howItWorks: [
      {
        step: '01',
        title: 'Enter Vehicle Details',
        description:
          'Tell us about your vehicle — make, model and year. It takes less than a minute.'
      },
      {
        step: '02',
        title: 'Compare Quotes',
        description:
          'Instantly see quotes from 50+ insurers side by side. Pick the cover that fits your budget.'
      },
      {
        step: '03',
        title: 'Get Covered',
        description:
          'Pay securely online and receive your policy document via email within minutes.'
      }
    ],
    whyChooseTitle: 'Why Choose FlexySure?',
    whyChooseSubtitle:
      'Get your motor insurance sorted in minutes — not days. We connect you with leading insurers so you always get the best deal.',
    features: [
      {
        title: 'Instant Quotes',
        description:
          'Compare motor insurance rates from multiple insurers in seconds.',
        badge: '50+',
        icon: 'shield'
      },
      {
        title: 'Quick Processing',
        description:
          'Get your policy issued within minutes, not days. Digital-first experience.',
        badge: '24H',
        icon: 'clock'
      },
      {
        title: 'Expert Support',
        description:
          'Dedicated insurance advisors to help you pick the right cover.',
        badge: '24/7',
        icon: 'users'
      },
      {
        title: 'Best Rates',
        description:
          'We negotiate with insurers so you always pay the lowest premium.',
        badge: '99%',
        icon: 'document'
      }
    ],
    productsTitle: 'Our Insurance Offerings',
    productsSubtitle:
      'Motor, Health, Home and more — pick the cover you need and get a quote in minutes',
    products: [],
    testimonialsTitle: 'What Our Customers Say',
    testimonialsSubtitle:
      'Trusted by over 100,000 vehicle owners across Africa.',
    testimonials: [
      {
        name: 'James Mwangi',
        role: 'Private Car Owner',
        quote:
          'FlexySure made getting motor insurance so easy. I compared quotes in seconds and saved 30% on my premium.',
        rating: 5
      },
      {
        name: 'Amina Hassan',
        role: 'Fleet Manager',
        quote:
          'Managing insurance for our fleet used to be a nightmare. FlexySure simplified everything — renewals, claims, tracking.',
        rating: 5
      },
      {
        name: 'Peter Odhiambo',
        role: 'PSV Operator',
        quote:
          'Competitive rates for my matatu. The claims process was smooth and the payout came through fast.',
        rating: 4
      }
    ],
    ctaTitle: 'Ready to Get Your Motor Insured?',
    ctaText:
      'Join over 100,000 vehicle owners who trust FlexySure for fast, affordable motor insurance.',
    ctaButtonText: 'Get a Quote',
    faqTitle: 'Frequently Asked Questions',
    faqSubtitle:
      'Everything you need to know about motor insurance with FlexySure.',
    faqItems: [
      {
        question: 'What types of motor insurance do you offer?',
        answer:
          'We offer Comprehensive, Third Party Fire & Theft, and Third Party Only covers for private vehicles, commercial vehicles and PSVs.'
      },
      {
        question: 'How fast can I get a quote?',
        answer:
          'Instantly. Enter your vehicle details and get quotes from 50+ insurers in under 60 seconds.'
      },
      {
        question: 'How do I file a claim?',
        answer:
          'Log in to your dashboard, click "File a Claim", upload the required documents and track your claim in real time.'
      },
      {
        question: 'Can I transfer my existing policy?',
        answer:
          'Yes. You can transfer your current motor policy to FlexySure at renewal. Our team will handle the paperwork for you.'
      }
    ],
    aboutTitle: 'FlexySure, Powered by Lucent',
    aboutDescription:
      "Kenya moves fast. Your insurance should too. We've ditched the bureaucracy and cut the wait times. Get instant quotes, fair prices, and claims that don't make you beg.",
    shareMessage: 'Great news! My vehicle is now insured with FlexySure!',
    copyrightHolder: 'FlexySure'
  },
  seo: {
    title: 'FlexySure — Compare & Buy Motor Insurance Online in Kenya',
    description:
      'FlexySure is a digital insurance aggregator. Compare side-by-side quotes from 50+ insurers and buy motor insurance for private cars, motorcycles and commercial vehicles instantly — no paperwork, no waiting.',
    keywords: [
      'motor insurance Kenya',
      'compare car insurance',
      'vehicle insurance quotes',
      'buy car insurance online',
      'insurance aggregator',
      'third party insurance',
      'comprehensive motor cover',
      'PSV insurance',
      'commercial vehicle insurance',
      'motorcycle insurance',
      'FlexySure'
    ],
    ogImage: flexysureLogo,
    locale: 'en_KE',
    areaServed: ['KE']
  },
  storagePrefix: 'flexysure',
  minVehicleValue: 500000
}

export default flexysureConfig
